/* eslint-disable @typescript-eslint/no-empty-object-type */
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/ui/_refactor/popup/modal';
import {
  useNotificationModalRootActionsStore,
  useNotificationModalRootStore,
} from '../notification-modal-root/notification-modal-root.store';
import { Checkbox } from '@/components/_shadcn-ui/checkbox';
import {
  useCareConfigurationNotificationQuery,
  GET_CARE_CONFIGURATION_NOTIFICATION_QUERY_KEY,
} from '../../../../notifications/api/get-care-configuration-notification.api';
import {
  useUpdateCareConfigurationNotificationMutation,
  UpdateCareConfigurationNotificationValidationSchema,
} from '../../../api/update-care-configuration-notification.api';
import type { TUpdateCareConfigurationNotificationValidation } from '../../../api/update-care-configuration-notification.api';
import { TCareConfigurationNotification } from '../../../types/notification.type';
import { queryClient } from '@/lib/react-query';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage } from '@/lib/axios';

const notificationConfig: NotificationConfig[] = [
  {
    key: 'NEW_CONVERSATION',
    label: 'New Case',
    description: 'Get notified when a new chat/conversation.',
    channels: ['app'],
  },
  // {
  //   key: 'CLOSE_CONVERSATION',
  //   label: 'Conversation Closed by Agent',
  //   description: 'Get notified when a conversation is closed by an agent.',
  //   channels: ['app'],
  // },
  {
    key: 'RESOLVED_CASE',
    label: 'Case Resolved',
    description: 'Get notified when an agent marks a case as resolved.',
    channels: ['email', 'app'],
  },
  {
    key: 'TEAM_MEMBER_ASSIGNMENT',
    label: 'New Agent Added to Team',
    description: 'Get notified when a new agent is added to the team.',
    channels: ['email', 'app'],
  },
  {
    key: 'TEAM_MEMBER_DELETED',
    label: 'Agent Removed from Team',
    description: 'Get notified when an agent is removed from the team.',
    channels: ['email', 'app'],
  },
  {
    key: 'NEW_TEAM_SUPERVISOR_ASSIGNMENT',
    label: 'New Supervisor Assigned',
    description: 'Get notified when a supervisor is assigned to your team.',
    channels: ['app'],
  },
  {
    key: 'TEAM_DELETED',
    label: 'Team Deleted',
    description: 'Get notified when a team is deleted.',
    channels: ['app'],
  },
  {
    key: 'CHANNEL_ADDED',
    label: 'Channel Integration Added',
    description: 'Get notified when a new channel integration is successfully added.',
    channels: ['app'],
  },
  {
    key: 'CHANNEL_DELETED',
    label: 'Channel Integration Removed',
    description: 'Get notified when an existing channel integration is deleted.',
    channels: ['app'],
  },
  {
    key: 'CHANNEL_DISCONNECT',
    label: 'Channel Integration Disconnected',
    description: 'Get notified when a channel integration becomes disconnected or fails.',
    channels: ['app'],
  },
  {
    key: 'LABEL_CUSTOMER_ADDED',
    label: 'Label Customer Added',
    description: 'Get notified when a new customer label is created in the system.',
    channels: ['app'],
  },
  {
    key: 'LABEL_CHAT_ADDED',
    label: 'Label Chat Added',
    description: 'Get notified when a new  label chat is created in the system.',
    channels: ['app'],
  },
  {
    key: 'LABEL_CASE_ADDED',
    label: 'Label Case Added',
    description: 'Get notified when a new  label case is created in the system.',
    channels: ['app'],
  },
  {
    key: 'LABEL_CUSTOMER_DELETED',
    label: 'Label Customer Deleted',
    description: 'Get notified when an existing label customer is removed from the system.',
    channels: ['app'],
  },
  {
    key: 'LABEL_CHAT_DELETED',
    label: 'Label Chat Deleted',
    description: 'Get notified when an existing label chat is removed from the system.',
    channels: ['app'],
  },
  {
    key: 'LABEL_CASE_DELETED',
    label: 'Label Case Deleted',
    description: 'Get notified when an existing label case is removed from the system.',
    channels: ['app'],
  },
  {
    key: 'LABEL_CUSTOMER_UPDATED',
    label: 'Label Customer Updated',
    description: 'Get notified when label customer linked to cases or conversations are updated.',
    channels: ['app'],
  },
  {
    key: 'LABEL_CHAT_UPDATED',
    label: 'Label Chat Updated',
    description: 'Get notified when label chat linked to cases or conversations are updated.',
    channels: ['app'],
  },
  {
    key: 'LABEL_CASE_UPDATED',
    label: 'Label Case Updated',
    description: 'Get notified when label case linked to cases or conversations are updated.',
    channels: ['app'],
  },
  {
    key: 'CHANNELS_AUTO_LABEL_UPDATED',
    label: 'Channels Auto Label Updated',
    description: 'Get notified when channels auto label is updated.',
    channels: ['app'],
  },
  {
    key: 'AUTO_LABEL_UPDATED',
    label: 'Auto Label Updated',
    description: 'Get notified when auto label is updated.',
    channels: ['app'],
  },
  // {
  //   key: 'CSAT_UPDATED',
  //   label: 'CSAT Updated',
  //   description: 'Get notified when CSAT settings are updated.',
  //   channels: ['app'],
  // },
  // {
  //   key: 'CSAT_ENABLED',
  //   label: 'CSAT Enabled',
  //   description: 'Get notified when CSAT is enabled.',
  //   channels: ['app'],
  // },
  // {
  //   key: 'CSAT_DISABLED',
  //   label: 'CSAT Disabled',
  //   description: 'Get notified when CSAT is disabled.',
  //   channels: ['app'],
  // },
  // {
  //   key: 'SLA_UPDATED',
  //   label: 'SLA Updated',
  //   description: 'Get notified when SLA rules or configurations are updated.',
  //   channels: ['app'],
  // },
];

const getChangedFields = (
  original: TUpdateCareConfigurationNotificationValidation | null,
  current: TUpdateCareConfigurationNotificationValidation
): Partial<TUpdateCareConfigurationNotificationValidation> | null => {
  if (!original) return null;

  const changes: Partial<TUpdateCareConfigurationNotificationValidation> = {};

  (Object.keys(current) as Array<keyof TUpdateCareConfigurationNotificationValidation>).forEach((key) => {
    const originalConfig = original[key];
    const currentConfig = current[key];

    // Skip if original doesn't have this config
    if (!originalConfig || !currentConfig) return;

    // Check if any value changed
    const hasChanged = (Object.keys(currentConfig) as Array<keyof typeof currentConfig>).some(
      (field) => originalConfig[field] !== currentConfig[field]
    );

    if (hasChanged) {
      changes[key] = currentConfig;
    }
  });

  return Object.keys(changes).length > 0 ? changes : null;
};

// Pure function to check if all notification configs are selected
const isAllSelected = (configs: TUpdateCareConfigurationNotificationValidation): boolean => {
  const keys = Object.keys(configs) as Array<keyof TUpdateCareConfigurationNotificationValidation>;

  if (keys.length === 0) return false;

  return keys.every((key) => {
    const config = configs[key];
    if (!config) return false;

    // Check all available channels (app and/or email) are true
    const hasApp = 'app' in config;
    const hasEmail = 'email' in config;

    if (hasApp && hasEmail) {
      return config.app === true && config.email === true;
    } else if (hasApp) {
      return config.app === true;
    } else if (hasEmail) {
      return config.email === true;
    }

    return false;
  });
};

// Pure function to update all notification configs
const updateAllNotificationConfigs = (
  configs: TUpdateCareConfigurationNotificationValidation,
  checked: boolean
): TUpdateCareConfigurationNotificationValidation => {
  const updated: Partial<TUpdateCareConfigurationNotificationValidation> = {};

  (Object.keys(configs) as Array<keyof TUpdateCareConfigurationNotificationValidation>).forEach((key) => {
    const config = configs[key];
    if (config) {
      const updatedConfig = { ...config };
      if ('app' in updatedConfig) {
        updatedConfig.app = checked;
      }
      if ('email' in updatedConfig) {
        updatedConfig.email = checked;
      }
      updated[key] = updatedConfig;
    }
  });

  return updated as TUpdateCareConfigurationNotificationValidation;
};

export type TUpdateCareNotificationModalType = 'UpdateCareNotificationModal';
export type IUpdateCareNotificationModalProps = {};

// Types
interface NotificationConfig {
  key: keyof TCareConfigurationNotification['config'];
  label: string;
  description: string;
  channels: ('app' | 'email')[];
}

const UpdateCareNotificationModal = memo(() => {
  const notificationModalRootActions = useNotificationModalRootActionsStore();
  const showToast = useCustomToast();
  const isOpen = useNotificationModalRootStore(
    useShallow((state) => state.modals.some((m) => m.type === 'UpdateCareNotificationModal'))
  );

  const handleClose = useCallback(() => {
    notificationModalRootActions.closeModal('UpdateCareNotificationModal');
  }, [notificationModalRootActions]);

  // Fetch server data when modal opens
  const { data: serverData } = useCareConfigurationNotificationQuery({
    queryConfig: { enabled: isOpen },
  });

  // Mutation hook
  const { mutate: updateNotification, isPending } = useUpdateCareConfigurationNotificationMutation({
    mutationConfigOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_CARE_CONFIGURATION_NOTIFICATION_QUERY_KEY] });
        showToast({
          message: 'Notification updated successfully!',
          type: 'success',
        });
        handleClose();
      },
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error updating notification, please try again';
        showToast({ message: errorMessage, type: 'error' });
      },
    },
  });

  const { handleSubmit, watch, setValue, reset } = useForm<TUpdateCareConfigurationNotificationValidation>({
    resolver: zodResolver(UpdateCareConfigurationNotificationValidationSchema),
    defaultValues: {
      NEW_CONVERSATION: { app: false },
      CLOSE_CONVERSATION: { app: false },
      RESOLVED_CASE: { app: false, email: false },
      TEAM_MEMBER_ASSIGNMENT: { app: false, email: false },
      TEAM_MEMBER_DELETED: { app: false, email: false },
      NEW_TEAM_SUPERVISOR_ASSIGNMENT: { app: false },
      CHANNEL_ADDED: { app: false },
      CHANNEL_DELETED: { app: false },
      CHANNEL_DISCONNECT: { app: false },
      LABEL_CUSTOMER_ADDED: { app: false },
      LABEL_CHAT_ADDED: { app: false },
      LABEL_CASE_ADDED: { app: false },
      LABEL_CUSTOMER_DELETED: { app: false },
      LABEL_CHAT_DELETED: { app: false },
      LABEL_CASE_DELETED: { app: false },
      LABEL_CUSTOMER_UPDATED: { app: false },
      LABEL_CHAT_UPDATED: { app: false },
      LABEL_CASE_UPDATED: { app: false },
      CHANNELS_AUTO_LABEL_UPDATED: { app: false },
      AUTO_LABEL_UPDATED: { app: false },
      CSAT_UPDATED: { app: false },
      CSAT_ENABLED: { app: false },
      CSAT_DISABLED: { app: false },
      SLA_UPDATED: { app: false },
    },
  });

  // Reference to store the initial server data
  const [initialServerData, setInitialServerData] = useState<TUpdateCareConfigurationNotificationValidation | null>(
    null
  );

  // Sync form with server data when it arrives
  useEffect(() => {
    if (serverData?.payload?.config) {
      // Store the initial server data
      setInitialServerData(serverData.payload.config as TUpdateCareConfigurationNotificationValidation);
      // Reset form with server data
      reset(serverData.payload.config as TUpdateCareConfigurationNotificationValidation);
    }
  }, [serverData, reset]);

  const watchAll = watch() as TUpdateCareConfigurationNotificationValidation;
  const allSelected = isAllSelected(watchAll);

  // Function to get only changed fields compared to server data

  const onSubmit = (data: TUpdateCareConfigurationNotificationValidation) => {
    const changedData = getChangedFields(initialServerData, data);

    if (changedData) {
      updateNotification(changedData);
    } else {
      handleClose();
    }
  };

  // Handle Select All checkbox
  const handleSelectAll = (checked: boolean) => {
    const configs = watchAll;
    const updatedConfigs = updateAllNotificationConfigs(configs, checked);

    (Object.keys(updatedConfigs) as Array<keyof TUpdateCareConfigurationNotificationValidation>).forEach((key) => {
      setValue(key, updatedConfigs[key]!);
    });
  };

  // RENDER
  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalHeader>
        <ModalTitle>Manage Notification Care</ModalTitle>
        <ModalDescription>Select which notification types you want to enable for this care.</ModalDescription>
      </ModalHeader>
      <ModalContent>
        {/* Select All */}
        <div className="flex items-center ">
          <label className="flex items-center cursor-pointer gap-3">
            <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />
            <span className="text-sm font-medium text-gray-700">Select All</span>
          </label>
        </div>

        {/* Notification Items */}
        <div className="mt-[16px]">
          {notificationConfig.map((item) => (
            <div key={item.key} className="flex items-center justify-between py-[8px] border-b border-gray-200">
              <div className="flex-1">
                <h3 className="text-[12px] font-[400] text-[#25151E]">{item.label}</h3>
                <p className="text-[10px] font-[400] text-[#9D9D9C] mt-[4px]">{item.description}</p>
              </div>
              <div className="flex items-center gap-4 ml-[48px]">
                {item.channels.includes('email') && (
                  <label className="flex items-center cursor-pointer gap-2">
                    <Checkbox
                      checked={
                        (watch(item.key as keyof TUpdateCareConfigurationNotificationValidation) as { email?: boolean })
                          ?.email || false
                      }
                      onCheckedChange={(checked) => {
                        const currentConfig = watch(
                          item.key as keyof TUpdateCareConfigurationNotificationValidation
                        ) as { app?: boolean; email?: boolean } | undefined;
                        setValue(
                          item.key as keyof TUpdateCareConfigurationNotificationValidation,
                          {
                            ...currentConfig,
                            email: checked === true,
                          } as TUpdateCareConfigurationNotificationValidation[keyof TUpdateCareConfigurationNotificationValidation],
                          { shouldValidate: true }
                        );
                      }}
                    />
                    <span className="text-[10px] font-[700] cursor-pointer text-[#9D9D9C]">Email</span>
                  </label>
                )}
                {item.channels.includes('app') && (
                  <label className="flex items-center cursor-pointer gap-2">
                    <Checkbox
                      checked={
                        (watch(item.key as keyof TUpdateCareConfigurationNotificationValidation) as { app?: boolean })
                          ?.app || false
                      }
                      onCheckedChange={(checked) => {
                        const currentConfig = watch(
                          item.key as keyof TUpdateCareConfigurationNotificationValidation
                        ) as { app?: boolean; email?: boolean } | undefined;
                        setValue(
                          item.key as keyof TUpdateCareConfigurationNotificationValidation,
                          {
                            ...currentConfig,
                            app: checked === true,
                          } as TUpdateCareConfigurationNotificationValidation[keyof TUpdateCareConfigurationNotificationValidation],
                          { shouldValidate: true }
                        );
                      }}
                    />
                    <span className="text-[10px] font-[700] cursor-pointer text-[#9D9D9C]">App</span>
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      </ModalContent>
      <ModalFooter>
        <ModalConfirmButton className="px-[24px]" onClick={handleSubmit(onSubmit)} disabled={isPending}>
          {isPending ? 'Saving...' : 'Save'}
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

UpdateCareNotificationModal.displayName = 'UpdateCareNotificationModal';
export default UpdateCareNotificationModal;
